import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../config";
import { getAppTodayIso } from "../utils/appDateTime";
import { buildPositionStatsByLocation } from "./clientDashboardUtils";

function decodeToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("authToken") || localStorage.getItem("token");
  if (!token || token === "null" || token === "undefined" || !String(token).trim()) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

export default function useClientDashboard() {
  const tokenPayload = useMemo(() => decodeToken(), []);
  const userType = String(tokenPayload?.type || "").toLowerCase();
  const tokenClientId = tokenPayload?.client_id
    ? String(tokenPayload.client_id)
    : "";
  const isContactUser = userType === "contact" || userType === "supervisor";

  const [dateFrom, setDateFrom] = useState(getAppTodayIso);
  const [clients, setClients] = useState([]);
  const [clientIds, setClientIds] = useState(() => {
    if (tokenClientId) return [String(tokenClientId)];
    return [];
  });
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientsReady, setClientsReady] = useState(!isContactUser);
  const autoSelectDoneRef = useRef(false);

  const effectiveClientIds = useMemo(() => {
    if (isContactUser) {
      const selected = clientIds.map(String).filter(Boolean);
      if (selected.length) return selected;
      return clients.map((c) => String(c.id)).filter(Boolean);
    }
    if (tokenClientId) return [String(tokenClientId)];
    const stored = localStorage.getItem("clientId");
    if (stored && stored !== "null") return [stored];
    return [];
  }, [clientIds, clients, isContactUser, tokenClientId]);

  const loadClients = useCallback(async () => {
    if (!isContactUser) {
      setClientsReady(true);
      return;
    }
    try {
      const res = await fetch(
        `${BASE_URL}/api/contact/assigned-clients?search=`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );
      const json = await res.json();
      const list = (json.data || []).map((c) => ({
        id: c.id ?? c.clientId,
        name: c.name ?? c.clientName,
      }));
      setClients(list);
      if (!autoSelectDoneRef.current && list.length === 1) {
        autoSelectDoneRef.current = true;
        setClientIds([String(list[0].id)]);
      }
    } catch {
      setClients([]);
    } finally {
      setClientsReady(true);
    }
  }, [isContactUser]);

  const fetchShifts = useCallback(async () => {
    if (!clientsReady) return;

    if (!effectiveClientIds.length) {
      setShifts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("dateFrom", dateFrom);
      params.set("dateTo", dateFrom);
      params.set("client_ids", effectiveClientIds.join(","));

      const res = await fetch(
        `${BASE_URL}/api/client/schedule?${params.toString()}`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );
      const json = await res.json();
      setShifts(Array.isArray(json.data) ? json.data : []);
    } catch {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [clientsReady, dateFrom, effectiveClientIds]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const locationStats = useMemo(
    () => buildPositionStatsByLocation(shifts),
    [shifts],
  );

  const hasShifts = shifts.length > 0;

  return {
    dateFrom,
    setDateFrom,
    loading,
    locationStats,
    hasShifts,
    refresh: fetchShifts,
    needsClientSelection:
      isContactUser && clientsReady && !effectiveClientIds.length,
    clientsReady,
  };
}
